import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CalendarCheck, CircleDot, Mountain, Search, Download, Plus, Pencil, LayoutGrid, BarChart2, Trash2 } from 'lucide-react';
import { eventBatchAPI, trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import useListQueryParams from '../hooks/useListQueryParams';
import AdminDateRangeFilter from '../components/AdminDateRangeFilter.jsx';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import './ModuleAdmin.css';

const categoryPillClass = (name) => {
  if (!name) return 'events-pill-default';
  const n = name.toLowerCase();
  if (n.includes('snow')) return 'events-pill-blue';
  if (n.includes('himalayan') || n.includes('himalaya')) return 'events-pill-purple';
  if (n.includes('adventure')) return 'events-pill-green';
  return 'events-pill-default';
};

const EventBatches = () => {
  const navigate = useNavigate();
  const { query, setQuery } = useListQueryParams({
    page: '1',
    search: '',
    trekId: '',
    startFrom: '',
    startTo: '',
    sortOrder: 'desc'
  });
  const searchQuery = query.search || '';
  const trekFilter = query.trekId || '';
  const startFrom = query.startFrom || '';
  const startTo = query.startTo || '';
  const sortOrder = query.sortOrder || 'desc';
  const page = Number(query.page || 1);
  const PAGE_SIZE = 10;
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { permissions } = useAuth();
  const canManageEventBatches =
    permissions.includes('*') || permissions.includes('event_batches_manage');

  const [inventoryTrekOpen, setInventoryTrekOpen] = useState(false);
  const [inventoryTrekQuery, setInventoryTrekQuery] = useState('');

  const trekListSearch = inventoryTrekOpen && inventoryTrekQuery.trim()
    ? inventoryTrekQuery.trim()
    : undefined;

  const { data: trekRes } = useQuery(
    ['treks', 'for-batches', trekListSearch || 'all'],
    () =>
      trekAPI.list({
        page: 1,
        limit: 500,
        sortBy: 'name',
        search: trekListSearch
      })
  );
  const treks = trekRes?.data?.data || [];

  const { data: selectedTrekRes } = useQuery(
    ['trek', 'for-batches-filter', trekFilter],
    () => trekAPI.get(trekFilter),
    { enabled: Boolean(trekFilter) }
  );
  const selectedTrekFromApi = selectedTrekRes?.data?.data;

  const { data, isLoading } = useQuery(
    ['event-batches', page, trekFilter, searchQuery, startFrom, startTo, sortOrder],
    () =>
      eventBatchAPI.list({
        page,
        limit: PAGE_SIZE,
        trekId: trekFilter || undefined,
        search: searchQuery.trim() || undefined,
        startFrom: startFrom || undefined,
        startTo: startTo || undefined,
        sortOrder,
        includeStats: true
      })
  );
  const items = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};
  const stats = data?.data?.meta?.stats;

  const totalEvents = stats?.totalEvents ?? pagination.total ?? items.length;
  const activeBatches = stats?.activeBatches ?? items.filter((b) => b.status === 'active').length;
  const upcomingTreks = stats?.upcomingTreks ?? items.filter((b) => b.status === 'upcoming').length;

  const totalPages = pagination.totalPages || 1;
  const paginatedItems = items;

  const selectedFilterTrek = useMemo(() => {
    if (selectedTrekFromApi && String(selectedTrekFromApi.id) === String(trekFilter)) {
      return selectedTrekFromApi;
    }
    return treks.find((t) => String(t.id) === String(trekFilter));
  }, [treks, trekFilter, selectedTrekFromApi]);

  const inventoryTrekChoices = useMemo(() => {
    const q = inventoryTrekQuery.trim().toLowerCase();
    const byId = new Map();
    for (const t of treks) {
      if (t?.id != null) byId.set(String(t.id), t);
    }
    if (
      selectedTrekFromApi?.id != null &&
      trekFilter &&
      !byId.has(String(selectedTrekFromApi.id))
    ) {
      byId.set(String(selectedTrekFromApi.id), selectedTrekFromApi);
    }
    let base = [...byId.values()].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );
    if (q) {
      base = base.filter((t) => {
        const hay = [t.name, t.location, t.code, t.category?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return base.slice(0, 150);
  }, [treks, inventoryTrekQuery, trekFilter, selectedTrekFromApi]);

  useEffect(() => {
    if (inventoryTrekOpen) return;
    setInventoryTrekQuery(selectedFilterTrek?.name || '');
  }, [trekFilter, selectedFilterTrek?.name, inventoryTrekOpen]);

  const deleteMutation = useMutation(
    (id) => eventBatchAPI.remove(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event-batches']);
        showToast({ message: 'Event batch deleted.', severity: 'success' });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to delete batch.',
          severity: 'error'
        })
    }
  );

  const toggleActiveMutation = useMutation(
    ({ id, isActive }) => eventBatchAPI.update(id, { isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event-batches']);
        showToast({ message: 'Batch updated.', severity: 'success' });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to update batch.',
          severity: 'error'
        })
    }
  );

  const handleToggleActive = (item) => {
    toggleActiveMutation.mutate({ id: item.id, isActive: !item.isActive });
  };

  return (
    <div className="module-page events-page">
      <PageHeader
        title="Events"
        subtitle="Manage and monitor all available trekking and adventure events."
      />
      <FilterBar contentSx={{ py: 1.75 }}>
        <div className="events-search-wrap">
          <Search size={18} className="events-search-icon" />
          <input
            className="events-search-input module-filter-input"
            type="text"
            placeholder="Search event code, trek name, region, or trek code..."
            value={searchQuery}
            onChange={(e) => setQuery({ search: e.target.value })}
          />
        </div>
        <AdminDateRangeFilter
          startDate={startFrom}
          endDate={startTo}
          onStartDateChange={(v) => setQuery({ startFrom: v, page: '1' })}
          onEndDateChange={(v) => setQuery({ startTo: v, page: '1' })}
          startLabel="Start from"
          endLabel="Start to"
        />
        {(startFrom || startTo) && (
          <button
            type="button"
            className="events-btn-export"
            onClick={() => setQuery({ startFrom: '', startTo: '', page: '1' })}
          >
            Clear dates
          </button>
        )}
        <select value={sortOrder} onChange={(e) => setQuery({ sortOrder: e.target.value })} className="module-filter-select">
          <option value="desc">Latest first</option>
          <option value="asc">Oldest first</option>
        </select>
        <button
          type="button"
          className="events-btn-export"
          onClick={() => {
            const rows = [
              ['Code', 'Event Name', 'Campsite', 'Region', 'Start Date', 'End Date', 'Status', 'Active'].join(',')
            ];
            items.forEach((item) => {
              rows.push([
                item.code || '',
                item.trek?.name || '',
                item.trek?.category?.name || '',
                item.trek?.location || '',
                item.startDate || '',
                item.endDate || '',
                item.status || '',
                item.isActive ? 'Yes' : 'No'
              ].map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','));
            });
            const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'events.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }}
        >
          <Download size={18} />
          Export CSV
        </button>
        {canManageEventBatches ? (
          <button
            className="module-btn"
            type="button"
            onClick={() => navigate('/event-batches/new')}
          >
            <Plus size={18} />
            Add New Event
          </button>
        ) : null}
      </FilterBar>

      <div className="events-summary-cards">
        <div className="events-summary-card events-card-total">
          <div className="events-summary-icon-wrap events-icon-blue">
            <CalendarCheck size={22} />
          </div>
          <span className="events-summary-label">Total Events</span>
          <span className="events-summary-value">{isLoading ? '...' : totalEvents}</span>
        </div>
        <div className="events-summary-card events-card-active">
          <div className="events-summary-icon-wrap events-icon-orange">
            <CircleDot size={22} />
          </div>
          <span className="events-summary-label">Active Batches</span>
          <span className="events-summary-value">{isLoading ? '...' : activeBatches}</span>
        </div>
        <div className="events-summary-card events-card-upcoming">
          <div className="events-summary-icon-wrap events-icon-green">
            <Mountain size={22} />
          </div>
          <span className="events-summary-label">Upcoming Treks</span>
          <span className="events-summary-value">{isLoading ? '...' : upcomingTreks}</span>
        </div>
      </div>

      <div className="module-card">
        <div className="events-inventory-head">
          <div>
            <h2 className="events-inventory-title">Events Inventory</h2>
            <p className="module-subtitle">Manage and monitor all trekking and adventure events. Use Edit to open the full form.</p>
          </div>
          <div className="events-inventory-filter">
            <label htmlFor="events-trek-filter-search">Trek:</label>
            <div className="events-form-trek-wrap">
              <input
                id="events-trek-filter-search"
                type="text"
                className="events-trek-search-input"
                placeholder="All treks — type name, region, or code"
                autoComplete="off"
                value={inventoryTrekQuery}
                onChange={(e) => setInventoryTrekQuery(e.target.value)}
                onFocus={() => setInventoryTrekOpen(true)}
                onBlur={() => {
                  setTimeout(() => setInventoryTrekOpen(false), 180);
                }}
              />
              {inventoryTrekOpen && (
                <div className="events-trek-dropdown" role="listbox">
                  <button
                    type="button"
                    className={`events-trek-dropdown-item ${!trekFilter ? 'selected' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setQuery({ trekId: '' });
                      setInventoryTrekQuery('');
                      setInventoryTrekOpen(false);
                    }}
                  >
                    All treks
                  </button>
                  {inventoryTrekChoices.length === 0 ? (
                    <div className="events-trek-dropdown-empty">No treks match</div>
                  ) : (
                    inventoryTrekChoices.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={`events-trek-dropdown-item ${String(trekFilter) === String(t.id) ? 'selected' : ''}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setQuery({ trekId: String(t.id) });
                          setInventoryTrekQuery(t.name || '');
                          setInventoryTrekOpen(false);
                        }}
                      >
                        {t.name}
                        {t.location ? ` · ${t.location}` : ''}
                        {t.code ? ` (${t.code})` : ''}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {(searchQuery.trim() || startFrom || startTo || trekFilter) && !isLoading && (
          <p className="events-filter-hint">
            {trekFilter && selectedFilterTrek ? (
              <>
                Trek “{selectedFilterTrek.name}”
                {totalEvents != null ? ` — ${totalEvents} batch${totalEvents === 1 ? '' : 'es'}` : ''}
                {(searchQuery.trim() || startFrom || startTo) ? '. ' : '.'}
              </>
            ) : null}
            {searchQuery.trim() ? (
              <>
                Search “{searchQuery.trim()}”
                {totalEvents != null ? ` — ${totalEvents} batch${totalEvents === 1 ? '' : 'es'} found` : ''}
                {(startFrom || startTo) ? '. ' : '.'}
              </>
            ) : null}
            {(startFrom || startTo) ? (
              <>
                Showing events with start date
                {startFrom && startTo && startFrom === startTo
                  ? ` on ${startFrom}`
                  : ` from ${startFrom || '…'} to ${startTo || '…'}`}
                {totalEvents != null && !searchQuery.trim() ? ` (${totalEvents} match${totalEvents === 1 ? '' : 'es'})` : ''}.
              </>
            ) : null}
          </p>
        )}
        {isLoading ? (
          <p className="module-loading">Loading events...</p>
        ) : !paginatedItems.length ? (
          <EmptyState
            title="No events found"
            description="Try adjusting search, date filters, or add a new event batch."
            actionLabel={canManageEventBatches ? 'Add New Event' : undefined}
            onAction={canManageEventBatches ? () => navigate('/event-batches/new') : undefined}
          />
        ) : (
          <>
            <table className="module-table events-inventory-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Event Name</th>
                  <th>Campsite</th>
                  <th>Region</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Status</th>
                  <th>Active</th>
                  {canManageEventBatches && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.code}</td>
                    <td>{item.trek?.name || '-'}</td>
                    <td>
                      {item.trek?.category?.name ? (
                        <span className={`events-category-pill ${categoryPillClass(item.trek.category.name)}`}>
                          {item.trek.category.name}
                        </span>
                      ) : '-'}
                    </td>
                    <td>{item.trek?.location || '-'}</td>
                    <td>{item.startDate}</td>
                    <td>{item.endDate}</td>
                    <td>{item.status}</td>
                    <td>
                      <label className="events-toggle">
                        <input
                          type="checkbox"
                          checked={item.status === 'completed' ? false : !!item.isActive}
                          onChange={() => {
                            if (item.status === 'completed') return;
                            handleToggleActive(item);
                          }}
                          disabled={!canManageEventBatches || item.status === 'completed'}
                        />
                        <span className="events-toggle-slider" />
                      </label>
                    </td>
                    {canManageEventBatches && (
                      <td className="events-actions-cell">
                        <button
                          type="button"
                          className="events-action-icon"
                          onClick={() => navigate(`/event-batches/${item.id}/edit`)}
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="events-action-icon"
                          title="Open editor"
                          onClick={() => navigate(`/event-batches/${item.id}/edit`)}
                        >
                          <LayoutGrid size={16} />
                        </button>
                        <button type="button" className="events-action-icon" title="Analytics">
                          <BarChart2 size={16} />
                        </button>
                        <button
                          type="button"
                          className="events-action-icon events-action-danger"
                          title="Delete"
                          onClick={() => { if (window.confirm('Delete batch?')) deleteMutation.mutate(item.id); }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {totalEvents > 0 ? (
              <div className="trek-inventory-pagination">
                <span>
                  Showing {(page - 1) * PAGE_SIZE + 1}
                  {' '}
                  to
                  {' '}
                  {Math.min(page * PAGE_SIZE, totalEvents)}
                  {' '}
                  of
                  {' '}
                  {totalEvents}
                  {' '}
                  events.
                </span>
                <div className="trek-pagination-btns">
                  <button
                    type="button"
                    className="module-btn secondary"
                    disabled={page <= 1}
                    onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}
                  >
                    Previous
                  </button>
                  <span className="trek-page-num">Page {page} of {totalPages}</span>
                  <button
                    type="button"
                    className="module-btn secondary"
                    disabled={page >= totalPages}
                    onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default EventBatches;
